import { ZODIAC_SIGNS } from './zodiacData'
import { NAVAGRAHA_PLANETS } from './navagrahaData'
import { ANGLES_12, polarPoint } from './polar'
import ZodiacGlyph from './ZodiacGlyph'

function OuterZodiacRing() {
  return (
    <div className="absolute inset-0 animate-spin-110-reverse">
      <svg viewBox="0 0 800 800" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="400" cy="400" r="392" stroke="#F59E0B" strokeOpacity="0.08" strokeWidth="1" strokeDasharray="2 8" />
        <circle cx="400" cy="400" r="372" stroke="#D97706" strokeOpacity="0.16" strokeWidth="1.2" />
        <circle cx="400" cy="400" r="330" stroke="#D97706" strokeOpacity="0.12" strokeWidth="1" />
        {ANGLES_12.map((deg) => {
          const inner = polarPoint(400, 400, 330, deg)
          const outer = polarPoint(400, 400, 372, deg)
          return (
            <line
              key={deg}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke="#D97706"
              strokeOpacity="0.18"
              strokeWidth="1.2"
            />
          )
        })}
        {ANGLES_12.map((deg, i) => {
          const p = polarPoint(400, 400, 351, deg)
          return (
            <ZodiacGlyph
              key={deg}
              sign={ZODIAC_SIGNS[i].sign}
              className="text-[#D97706] opacity-[0.28]"
              x={p.x - 8}
              y={p.y - 8}
              width="16"
              height="16"
            />
          )
        })}
      </svg>
    </div>
  )
}

function MiddleAspectRing() {
  const aspectLines = [
    [300, 10, 300, 590],
    [10, 300, 590, 300],
    [551, 155, 49, 445],
    [551, 445, 49, 155],
    [445, 49, 155, 551],
    [445, 551, 155, 49],
  ]
  return (
    <div className="absolute inset-[10%] animate-spin-140-normal">
      <svg viewBox="0 0 600 600" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="300" cy="300" r="290" stroke="#F59E0B" strokeOpacity="0.10" strokeWidth="1" strokeDasharray="3 11" />
        <circle cx="300" cy="300" r="258" stroke="#D97706" strokeOpacity="0.08" strokeWidth="0.8" />
        {aspectLines.map(([x1, y1, x2, y2], i) => (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#F59E0B"
            strokeOpacity={i < 2 ? '0.07' : '0.06'}
            strokeWidth={i < 2 ? '0.7' : '0.6'}
          />
        ))}
        {ANGLES_12.map((deg) => {
          const p = polarPoint(300, 300, 290, deg)
          return <circle key={deg} cx={p.x} cy={p.y} r="2.5" fill="#F59E0B" fillOpacity="0.22" />
        })}
      </svg>
    </div>
  )
}

function InnerOrnamentalRing() {
  return (
    <div className="absolute inset-[28%] animate-spin-70-reverse">
      <svg viewBox="0 0 400 400" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="200" cy="200" r="190" stroke="#F59E0B" strokeOpacity="0.13" strokeWidth="1" strokeDasharray="4 8" />
        <circle cx="200" cy="200" r="170" stroke="#B45309" strokeOpacity="0.08" strokeWidth="0.7" />
        <line x1="200" y1="10" x2="200" y2="390" stroke="#F59E0B" strokeOpacity="0.08" strokeWidth="0.7" />
        <line x1="10" y1="200" x2="390" y2="200" stroke="#F59E0B" strokeOpacity="0.08" strokeWidth="0.7" />
        <line x1="66" y1="66" x2="334" y2="334" stroke="#F59E0B" strokeOpacity="0.06" strokeWidth="0.6" />
        <line x1="334" y1="66" x2="66" y2="334" stroke="#F59E0B" strokeOpacity="0.06" strokeWidth="0.6" />
        <polygon points="200,4 206,10 200,16 194,10" fill="#F59E0B" fillOpacity="0.24" />
        <polygon points="390,194 396,200 390,206 384,200" fill="#F59E0B" fillOpacity="0.24" />
        <polygon points="200,384 206,390 200,396 194,390" fill="#F59E0B" fillOpacity="0.24" />
        <polygon points="4,194 10,200 4,206 -2,200" fill="#F59E0B" fillOpacity="0.24" />
      </svg>
    </div>
  )
}

function PlanetIcon({ planet }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
      {planet.circle && <circle cx={planet.circle.cx} cy={planet.circle.cy} r={planet.circle.r} />}
      {planet.paths?.map((d, i) => (
        <path key={`p${i}`} d={d} />
      ))}
      {planet.lines?.map(([x1, y1, x2, y2], i) => (
        <line key={`l${i}`} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
      {planet.polylines?.map((pts, i) => (
        <polyline key={`pl${i}`} points={pts.join(',')} />
      ))}
    </svg>
  )
}

function NavagrahaLayer() {
  return (
    <div className="absolute inset-0" style={{ zIndex: 2 }}>
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="25" stroke="#F59E0B" strokeOpacity="0.09" strokeWidth="0.22" strokeDasharray="0.7 2.2" />
      </svg>

      {NAVAGRAHA_PLANETS.map((planet) => (
        <div className="ow-planet" style={{ top: planet.top, left: planet.left }} key={planet.name}>
          <div className="ow-disc" title={`${planet.name} — ${planet.label}`}>
            <PlanetIcon planet={planet} />
          </div>
          <span className="ow-lbl">{planet.name}</span>
        </div>
      ))}
    </div>
  )
}

export default function HoroscopeWheelBackground() {
  return (
    <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-amber-50/60 blur-3xl" />
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-orange-50/50 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-yellow-50/50 blur-3xl" />

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[min(760px,92vw)] h-[min(760px,92vw)]">
          <OuterZodiacRing />
          <MiddleAspectRing />
          <InnerOrnamentalRing />
          <NavagrahaLayer />

          <div className="absolute inset-[44%] rounded-full bg-amber-100/50 blur-md" />
          <div className="absolute inset-[46%] rounded-full bg-amber-50/70 blur-sm" />
        </div>
      </div>
    </div>
  )
}
